import { parseDate } from "@internationalized/date";
import { type Moment, moment } from "@msuite/utils/date";
import { cva } from "class-variance-authority";
import {
	Calendar as AriaCalendar,
	CalendarCell,
	CalendarGrid,
	CalendarGridBody,
	CalendarGridHeader,
	CalendarHeaderCell,
	DatePicker,
} from "react-aria-components";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { Button } from "../button";
import { cn, Fragment } from "../utils";
import { DateInputCalendarHeader } from "./date-input-calendar-header";

/** Props Interface */
interface DateInputCalendarProps<T extends FieldValues> {
	control: Control<T>;
	name: Path<T>;
	minDate?: Date | Moment | string;
	maxDate?: Date | Moment | string;
	isNullable?: boolean;
	highlightRange?: { start?: string | null; end?: string | null };
}

export function DateInputCalendar<T extends FieldValues>({
	control,
	name,
	minDate,
	maxDate,
	isNullable,
	highlightRange,
}: DateInputCalendarProps<T>) {
	/** Computed */
	const rangeStart = highlightRange?.start ? parseDate(moment(highlightRange.start).db()) : null;
	const rangeEnd = highlightRange?.end ? parseDate(moment(highlightRange.end).db()) : null;

	/** Render */
	return (
		<Controller
			control={control}
			name={name}
			render={({ field: { onChange, value } }) => (
				<DatePicker
					value={value ? parseDate(moment(value).db()) : null}
					onChange={(date) => onChange(date ? date.toString() : null)}
					firstDayOfWeek="mon"
					minValue={minDate ? parseDate(moment(minDate).db()) : undefined}
					maxValue={maxDate ? parseDate(moment(maxDate).db()) : undefined}
				>
					<AriaCalendar>
						<DateInputCalendarHeader />
						<CalendarGrid
							className="border-spacing-0"
							weekdayStyle="narrow"
							translate="yes"
						>
							<CalendarGridHeader className="py-2">
								{(day) => (
									<Fragment>
										<CalendarHeaderCell className="font-light text-xs">{day}</CalendarHeaderCell>
									</Fragment>
								)}
							</CalendarGridHeader>
							<CalendarGridBody>
								{(date) => {
									const isInRange =
										rangeStart !== null &&
										rangeEnd !== null &&
										date.compare(rangeStart) >= 0 &&
										date.compare(rangeEnd) <= 0;

									return (
										<Fragment>
											<CalendarCell
												date={date}
												className={itemClasses({ isInRange })}
											>
												{({ formattedDate }) => {
													return <span>{formattedDate.padStart(2, "0")}</span>;
												}}
											</CalendarCell>
										</Fragment>
									);
								}}
							</CalendarGridBody>
						</CalendarGrid>
					</AriaCalendar>
					{isNullable && (
						<div className="w-full h-stack justify-end mt-2 py-2 border-t-[0.5px] border-color">
							<Button
								onClick={() => {
									onChange(null);
								}}
								size="xxs"
								variant="ghost"
							>
								SET NULL
							</Button>
						</div>
					)}
				</DatePicker>
			)}
		/>
	);
}

const itemClasses = cva(
	cn(
		"tabular-nums",
		"aspect-square px-2.5 flex-center rounded-full",
		"data-focused:input-focused",
		"relative",
		"border border-transparent!",
		"data-focused:border-accent-700!",
		"data-outside-visible-range:opacity-20",
		"data-selected:bg-accent-700 data-selected:text-white",
		"data-disabled:opacity-20",
		"border-box",
		"text-xs",
		"cursor-pointer",
	),
	{
		variants: {
			isInRange: {
				true: "bg-accent-200/40 dark:bg-accent-800/20",
				false: "",
			},
		},
	},
);
